// ── Employee Performance widget: the drill-down's data ─────────────────
//
// GET /api/dashboard/employee-performance/transactions takes the widget's own parameters plus an
// employeeId, and answers with that employee's receipts for the same view — newest first, capped —
// and totals over every one of them, so the totals are the employee's row in the widget. Nothing
// about the customer comes back.

import {
    EmployeePerformanceError,
    employeePerformanceParams,
    fetchEmployeeJson,
    type EmployeePerformanceFilters,
    type EmployeePerformancePeriod,
} from './employeePerformance'

export interface EmployeeTransaction {
    transactionId: string
    /** The till's own clock: yyyy-MM-ddTHH:mm:ss, with no offset. */
    time: string
    storeId: string | null
    storeName: string | null
    till: number | null
    receiptNumber: string | null
    channel: 'instore' | 'online'
    units: number
    /** After line discounts — Gross Sales when returns are excluded, as on the widget. */
    netSales: number
    discounts: number
    /** Adventure Bucks credited on this receipt. */
    bucksCredited: number
    /** Some line on the receipt was a return (even one GROSS leaves out of the figures). */
    hasReturn: boolean
}

export interface EmployeeTransactionTotals {
    transactions: number
    units: number
    netSales: number
    discounts: number
    bucksCredited: number
}

export interface EmployeeTransactionsData {
    employeeId: string
    /** Inclusive yyyy-MM-dd bounds of the window the server queried. */
    startDate: string
    endDate: string
    /** The newest receipts, at most `limit`. */
    transactions: EmployeeTransaction[]
    /** Every matching receipt, including any the list stopped short of. */
    totals: EmployeeTransactionTotals
    /** More receipts matched than are listed. */
    truncated: boolean
    limit: number
}

/** The widget's own query string, plus the employee — so the drill-down covers exactly its view. */
export function buildEmployeeTransactionsUrl(
    period: EmployeePerformancePeriod, filters: EmployeePerformanceFilters, employeeId: string,
): string {
    const p = employeePerformanceParams(period, filters)
    p.set('employeeId', employeeId)
    return `/api/dashboard/employee-performance/transactions?${p.toString()}`
}

export function fetchEmployeeTransactions(url: string, token: string | null): Promise<EmployeeTransactionsData> {
    return fetchEmployeeJson<EmployeeTransactionsData>(url, token)
}

/** The user-facing sentence for a failed load; never echoes server internals. */
export function describeEmployeeTransactionsError(err: unknown): string {
    if (err instanceof EmployeePerformanceError) {
        if (err.status === 403) return err.message || "You don't have access to these transactions."
        if (err.status === 400) return err.message || 'These filters can’t be shown as transactions.'
    }
    return 'The transactions could not be loaded. Close this and try again.'
}

const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
const dayFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const dayYearFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * A receipt's time as the drill-down lists it: the time alone when the view is one day, with the
 * date when it is longer, and the year too when it spans years. Built from the parts rather than
 * parsed, so the browser's time zone never shifts the till's clock.
 */
export function formatTransactionTime(time: string, startDate: string, endDate: string): string {
    const [day, clock = '00:00:00'] = time.split('T')
    const [y, mo, d] = day.split('-').map(Number)
    const [h, mi] = clock.split(':').map(Number)
    const at = new Date(y, mo - 1, d, h, mi)
    if (Number.isNaN(at.getTime())) return time
    // Some ICU versions put a narrow no-break space before AM/PM; every browser shows the same text.
    const clockText = timeFmt.format(at).replace(/ /g, ' ')
    if (startDate === endDate) return clockText
    const date = (startDate.slice(0, 4) === endDate.slice(0, 4) ? dayFmt : dayYearFmt).format(at)
    return `${date}, ${clockText}`
}
